import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, Clock, MapPin, MessageCircle, ArrowRight, ExternalLink, CheckCircle, Info } from 'lucide-react';

// ── Contact.jsx imports ENQUIRIES_WEBHOOK_URL from shared config ──
import { ENQUIRIES_WEBHOOK_URL } from '../config/webhooks';

const WA_URL            = 'https://wa.me/919246887272?text=Hi%2C%20I%20have%20a%20question%20about%20Sri%20Aurobindo%20Montessori%20School';
const WA_ADMISSIONS_URL = 'https://wa.me/919246887272?text=Hi%2C%20I%20am%20interested%20in%20booking%20a%20school%20tour%20at%20Sri%20Aurobindo%20Montessori%20School';
const MAPS_URL          = 'https://maps.app.goo.gl/QtH4u8sFkXHJJFCWA';

const neighborhoods = [
  'East Marredpally', 'Trimulgherry', 'Karkhana', 'Bowenpally', 'Dairy Farm Road',
  'Sikh Village', 'Begumpet', 'Picket', 'Paradise', 'Sindhi Colony',
  'Diamond Point', 'Centre Point', 'Alwal',
];

const SUBJECT_OPTIONS = [
  { value: 'A general question',                   label: 'A general question' },
  { value: 'Visiting the school / directions',      label: 'Visiting the school / directions' },
  { value: 'Admissions / joining the school',       label: 'Admissions / joining the school' },
  { value: "Careers — I'm a Montessori teacher",    label: "Careers — I'm a Montessori teacher" },
  { value: 'Partnership / vendor',                  label: 'Partnership / vendor' },
  { value: 'Something else',                        label: 'Something else' },
];

const MESSAGE_MIN = 20;
const MESSAGE_MAX = 500;

const initialForm = {
  name: '',
  phone: '',
  email: '',
  subject: '',
  message: '',
  website: '', // honeypot — must stay empty
};

const inputClass =
  'w-full rounded-2xl border-2 border-blue-100 bg-[#F4F7FB] px-5 py-3.5 text-slate-800 placeholder-slate-400 focus:border-[#60A5FA] focus:ring-4 focus:ring-blue-100 focus:outline-none transition-all text-sm';
const labelClass = 'block text-sm font-semibold text-[#1E3A8A] mb-1.5';
const errorClass = 'text-[#B45309] text-xs mt-1.5 flex items-center gap-1';

// Indian mobile: optional +91/0 prefix, then 6–9 starting 10-digit number
const MOBILE_RE = /^(\+91[\s-]?|0)?[6-9]\d{9}$/;

export default function Contact() {
  const [form, setForm]             = useState(initialForm);
  const [submitted, setSubmitted]   = useState(false);
  const [errors, setErrors]         = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const validate = () => {
    const e = {};
    if (!form.name.trim())                             e.name    = 'Please tell us your name.';
    if (!form.phone.trim())                            e.phone   = 'A mobile number helps us reach you.';
    else if (!MOBILE_RE.test(form.phone.trim()))       e.phone   = 'Please enter a valid 10-digit Indian mobile number.';
    if (!form.subject)                                 e.subject = 'Please let us know what this is about.';
    if (!form.message.trim())                          e.message = 'Please write us a few lines so we know how to help.';
    else if (form.message.trim().length < MESSAGE_MIN) e.message = 'Please write us a few lines so we know how to help.';
    return e;
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Honeypot check — bots fill this hidden field; humans don't
    if (form.website) return;

    const v = validate();
    if (Object.keys(v).length > 0) { setErrors(v); return; }

    setSubmitting(true);
    setSubmitError('');
    try {
      if (ENQUIRIES_WEBHOOK_URL) {
        const payload = {
          source:       'Contact',
          enquiryType:  form.subject,
          name:         form.name,
          mobile:       form.phone,
          email:        form.email,
          message:      form.message,
          childDob:     '',
          program:      '',
        };
        await fetch(ENQUIRIES_WEBHOOK_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(payload),
          mode:    'no-cors',
        });
      }
      setSubmitted(true);
    } catch (_) {
      setSubmitError('Something went wrong — please try again, or WhatsApp us at +91\u00a092468\u00a087272.');
    } finally {
      setSubmitting(false);
    }
  };

  const isAdmissionsSelected = form.subject === 'Admissions / joining the school';
  const msgLen = form.message.length;

  return (
    <div className="pt-16">
      <Helmet>
        <title>Contact &amp; Visit Us | Sri Aurobindo Montessori, West Marredpally, Secunderabad</title>
        <meta name="description" content="Visit Sri Aurobindo Montessori House of Children in West Marredpally, Secunderabad. Address, map, phone, WhatsApp and school-tour booking. We'd love to show you around." />
      </Helmet>

      {/* ── BANNER ── */}
      <div className="bg-[#1E3A8A] py-20 lg:py-28">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">Contact</p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-5 leading-tight"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="contact-h1"
          >
            Come say hello.
          </h1>
          <p className="text-blue-200 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
            Whether you have one small question or a hundred, we're easy to reach — and genuinely happy to talk.
          </p>
        </div>
      </div>

      {/* ── REACH US + MAP ── */}
      <section className="py-20 bg-white" data-testid="reach-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14">

            {/* Left: Contact details */}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Details</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-8" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Reach us
              </h2>

              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-[#F4F7FB] rounded-2xl p-5" data-testid="contact-address">
                  <div className="w-10 h-10 bg-[#DBEAFE] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#1E3A8A]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1E3A8A] text-sm mb-1">Address</p>
                    <p className="text-slate-600 text-sm leading-relaxed">
                      62, Krishnapuri Colony,<br />
                      W Marredpally Rd, Krishnapuri Colony,<br />
                      Lakshmi Nagar, West Marredpally,<br />
                      Secunderabad, Telangana 500026
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-[#F4F7FB] rounded-2xl p-5" data-testid="contact-phones">
                  <div className="w-10 h-10 bg-[#DBEAFE] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone size={18} className="text-[#1E3A8A]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1E3A8A] text-sm mb-1">Phone / WhatsApp</p>
                    <a href="tel:9246887272" data-testid="contact-phone1" className="block text-slate-600 text-sm hover:text-[#1E3A8A] transition-colors font-medium">9246887272</a>
                    <a href="tel:9849069579" data-testid="contact-phone2" className="block text-slate-600 text-sm hover:text-[#1E3A8A] transition-colors font-medium">9849069579</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-[#F4F7FB] rounded-2xl p-5" data-testid="contact-email">
                  <div className="w-10 h-10 bg-[#DBEAFE] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail size={18} className="text-[#1E3A8A]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1E3A8A] text-sm mb-1">Email</p>
                    <a href="mailto:sriaurobindo60@gmail.com" className="text-slate-600 text-sm hover:text-[#1E3A8A] transition-colors break-all">
                      sriaurobindo60@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-[#F4F7FB] rounded-2xl p-5" data-testid="contact-hours">
                  <div className="w-10 h-10 bg-[#DBEAFE] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Clock size={18} className="text-[#1E3A8A]" />
                  </div>
                  <div>
                    <p className="font-bold text-[#1E3A8A] text-sm mb-1">School Hours</p>
                    <p className="text-slate-600 text-sm">Monday – Saturday</p>
                    <p className="text-slate-700 font-semibold text-sm">9:30 am – 3:30 pm</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <a
                  href={WA_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] text-white rounded-full py-4 font-bold text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
                >
                  <MessageCircle size={18} /> WhatsApp Us
                </a>
                <Link
                  to="/admissions#enquiry-form"
                  data-testid="contact-enquire-btn"
                  className="flex-1 flex items-center justify-center gap-2 bg-[#1E3A8A] text-white rounded-full py-4 font-bold text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm"
                >
                  Enquire Now <ArrowRight size={16} />
                </Link>
              </div>
            </div>

            {/* Right: Map + neighborhoods */}
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Location</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-6" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Find Us
              </h2>
              <div
                className="rounded-3xl overflow-hidden shadow-[0_8px_40px_rgba(30,58,138,0.12)] relative h-80 sm:h-96"
                data-testid="map-embed"
              >
                <iframe
                  title="Sri Aurobindo Montessori School Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3806.1230620106207!2d78.50138307956801!3d17.453823058223247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bcb9a4774d50e31%3A0xc4cf9edfde2e0347!2sSri%20Aurobindo%20Montessori%20School!5e0!3m2!1sen!2sin!4v1786006704852!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              </div>
              <div className="flex justify-between items-center mt-3 px-1">
                <p className="text-slate-400 text-xs">62, Krishnapuri Colony, West Marredpally, Secunderabad</p>
                <a
                  href={MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="view-on-maps-btn"
                  className="inline-flex items-center gap-1.5 text-[#1E3A8A] text-xs font-bold hover:underline"
                >
                  Open in Maps <ExternalLink size={12} />
                </a>
              </div>
              <div className="mt-5 bg-[#F4F7FB] rounded-2xl p-5" data-testid="neighborhoods-block">
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-[#60A5FA] mb-2">Serving families across</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Most of our student families come from within 4–5 km of West Marredpally —{' '}
                  <span className="text-slate-700 font-medium">{neighborhoods.join(', ')}</span>{' '}
                  and surrounding areas.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── BOOK YOUR VISIT ── */}
      <section className="py-20 bg-[#1E3A8A]" data-testid="book-visit-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-300 mb-4">School Visits</p>
          <h2
            className="text-3xl sm:text-4xl font-extrabold text-white mb-5"
            style={{ fontFamily: 'Nunito, sans-serif' }}
            data-testid="book-visit-h2"
          >
            Book your visit
          </h2>
          <p className="text-blue-200 text-base leading-relaxed mb-10 max-w-xl mx-auto">
            School tours are by appointment, Monday to Friday during school hours. Current parents may also book child-observation visits a day in advance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={WA_ADMISSIONS_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="book-visit-whatsapp-btn"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-black text-sm hover:bg-[#128C7E] transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              <MessageCircle size={16} /> WhatsApp Us
            </a>
            <Link
              to="/admissions#enquiry-form"
              data-testid="book-visit-tour-btn"
              className="bg-white text-[#1E3A8A] rounded-full px-8 py-4 font-black text-sm hover:bg-blue-50 transition-all duration-200 hover:-translate-y-0.5 shadow-md inline-flex items-center justify-center gap-2"
            >
              Book a Tour <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── REACH OUT FORM ── */}
      <section className="py-16 bg-[#F4F7FB]" id="contact-form" data-testid="contact-form-section">
        <div className="max-w-xl mx-auto px-4 sm:px-6">

          {submitted ? (
            /* ── SUCCESS STATE ── */
            <div
              className="bg-white rounded-3xl p-10 text-center shadow-[0_4px_30px_rgba(59,130,246,0.09)] border border-blue-50"
              data-testid="contact-form-success"
            >
              <div className="w-16 h-16 bg-[#EFF6FF] rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle size={30} className="text-[#1E3A8A]" />
              </div>
              <h3 className="text-2xl font-extrabold text-[#1E3A8A] mb-3" style={{ fontFamily: 'Nunito, sans-serif' }}>
                Thank you for writing to us!
              </h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-2">
                A real person — not an autoresponder — will reply within two working days.
              </p>
              <p className="text-slate-500 text-sm leading-relaxed mb-8">
                In a hurry? WhatsApp us at <strong className="text-[#1E3A8A]">+91 92468 87272</strong> — it's the fastest way to reach us.
              </p>
              <a
                href={WA_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-success-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-7 py-4 text-sm font-bold hover:bg-[#128C7E] transition-colors shadow-sm"
              >
                <MessageCircle size={16} /> Chat on WhatsApp
              </a>
            </div>
          ) : (
            /* ── FORM ── */
            <div>
              {/* Heading */}
              <div className="text-center mb-8">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#60A5FA] mb-3">Write to Us</p>
                <h2
                  className="text-2xl sm:text-3xl font-extrabold text-[#1E3A8A] mb-3"
                  style={{ fontFamily: 'Nunito, sans-serif' }}
                  data-testid="contact-form-heading"
                >
                  Reach Out to Us
                </h2>
                <p className="text-slate-500 text-sm leading-relaxed max-w-md mx-auto">
                  For admissions, use our{' '}
                  <Link to="/admissions#enquiry-form" className="text-[#1E3A8A] font-semibold hover:underline">
                    Admissions form
                  </Link>
                  . This form is for all other purposes — a question, a visit, a CV, an idea — whatever brings you here, write to us and a real person will reply.
                </p>
              </div>

              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl p-8 shadow-[0_4px_30px_rgba(59,130,246,0.08)] border border-blue-50"
                data-testid="contact-form"
                noValidate
              >
                {/* Honeypot — hidden from real users, bots fill it */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
                  <label htmlFor="website">Leave this empty</label>
                  <input
                    type="text"
                    id="website"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="space-y-5">

                  {/* 1. Name */}
                  <div>
                    <label htmlFor="cf-name" className={labelClass}>
                      Your name <span className="text-[#B45309]">*</span>
                    </label>
                    <input
                      id="cf-name"
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your full name"
                      data-testid="contact-input-name"
                      className={`${inputClass}${errors.name ? ' border-[#F59E0B]' : ''}`}
                      autoComplete="name"
                    />
                    {errors.name && (
                      <p className={errorClass} data-testid="error-name">
                        <Info size={12} /> {errors.name}
                      </p>
                    )}
                  </div>

                  {/* 2. Mobile */}
                  <div>
                    <label htmlFor="cf-phone" className={labelClass}>
                      Mobile number <span className="text-[#B45309]">*</span>
                    </label>
                    <input
                      id="cf-phone"
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="WhatsApp-preferred number"
                      data-testid="contact-input-phone"
                      className={`${inputClass}${errors.phone ? ' border-[#F59E0B]' : ''}`}
                      autoComplete="tel"
                    />
                    {errors.phone && (
                      <p className={errorClass} data-testid="error-phone">
                        <Info size={12} /> {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* 3. Email */}
                  <div>
                    <label htmlFor="cf-email" className={labelClass}>
                      Email{' '}
                      <span className="text-slate-400 font-normal">(optional)</span>
                    </label>
                    <input
                      id="cf-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="Optional — if you prefer replies by email"
                      data-testid="contact-input-email"
                      className={inputClass}
                      autoComplete="email"
                    />
                  </div>

                  {/* 4. Subject dropdown */}
                  <div>
                    <label htmlFor="cf-subject" className={labelClass}>
                      What's this about? <span className="text-[#B45309]">*</span>
                    </label>
                    <select
                      id="cf-subject"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      data-testid="contact-input-subject"
                      className={`${inputClass}${errors.subject ? ' border-[#F59E0B]' : ''}`}
                    >
                      <option value="" disabled>Choose one…</option>
                      {SUBJECT_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.subject && (
                      <p className={errorClass} data-testid="error-subject">
                        <Info size={12} /> {errors.subject}
                      </p>
                    )}

                    {/* 5. Conditional admissions nudge */}
                    {isAdmissionsSelected && (
                      <div
                        className="mt-3 bg-[#EFF6FF] border border-[#BFDBFE] rounded-2xl px-5 py-4"
                        data-testid="admissions-nudge"
                      >
                        <p className="text-[#1E3A8A] text-sm leading-relaxed">
                          Looking to enrol? Wonderful — our admissions form captures a few details about your child that help us guide you better.{' '}
                          <Link
                            to="/admissions#enquiry-form"
                            className="font-bold underline hover:text-[#1E40AF]"
                            data-testid="admissions-nudge-link"
                          >
                            Go to the admissions form →
                          </Link>
                          {' '}Or continue here and we'll take it from there.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 6. Message */}
                  <div>
                    <label htmlFor="cf-message" className={labelClass}>
                      Your message <span className="text-[#B45309]">*</span>
                    </label>
                    <textarea
                      id="cf-message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      rows={5}
                      maxLength={MESSAGE_MAX}
                      placeholder="Tell us what's on your mind — the more you share, the better we can help."
                      data-testid="contact-input-message"
                      className={`${inputClass} resize-none${errors.message ? ' border-[#F59E0B]' : ''}`}
                    />
                    <div className="flex justify-between items-start mt-1">
                      {errors.message ? (
                        <p className={errorClass} data-testid="error-message">
                          <Info size={12} /> {errors.message}
                        </p>
                      ) : (
                        <span />
                      )}
                      <p className={`text-xs tabular-nums ${msgLen > MESSAGE_MAX - 20 ? 'text-[#B45309]' : 'text-slate-400'}`}>
                        {msgLen} / {MESSAGE_MAX}
                      </p>
                    </div>
                  </div>

                </div>{/* end space-y-5 */}

                {/* Network error banner */}
                {submitError && (
                  <div className="mt-5 bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3" data-testid="contact-submit-error">
                    <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-amber-800 text-sm leading-relaxed">{submitError}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="mt-7 w-full bg-[#1E3A8A] text-white rounded-full py-4 font-black text-sm hover:bg-[#1E40AF] transition-all duration-200 hover:-translate-y-0.5 shadow-sm flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Sending…' : (
                    <>Send Message <ArrowRight size={16} /></>
                  )}
                </button>

                <p className="text-center text-slate-400 text-xs mt-4">
                  For admissions, use our{' '}
                  <Link to="/admissions#enquiry-form" className="text-[#1E3A8A] font-semibold hover:underline">
                    Admissions Enquiry Form
                  </Link>
                </p>
              </form>
            </div>
          )}

        </div>
      </section>

    </div>
  );
}
